import { IApiResponse } from '@interfaces/module.interface';

import {
  CreateUpdateTableAreaPayload,
  CreateUpdateTablesListPayload,
  IAreaGroupInterface,
  IPOSTableDetailsInterface,
  ITableAreaInterface,
} from '@modules/point-of-sale/interface/bo-interface/setUpTable-list.interface';
import {
  setTableAreas,
  setTableDetails,
  setTableList,
  stopIsLoadingTransferTable,
} from '@modules/point-of-sale/slice/pos-table-area.slice';

import { api } from '@services/api';
import { handleError } from '@utils/api-error-handling';
import { isValidUUID } from '@utils/format';
import { encryptPayload } from '@utils/security';

// #region API Endpoints
export const tableGroupAPI = api
  .enhanceEndpoints({
    addTagTypes: [
      'TableGroupList',
      'TableAreaList',
      'TableGroupDetails',
      'TableDetails',
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getAllTableGroupList: builder.query({
        query: (params?: string | null) =>
          `tables/groupings?filter[type]=${isValidUUID(params ?? '') ? '1' : params}`,
        providesTags: ['TableGroupList'],
        // async onQueryStarted(_arg, { queryFulfilled }) {
        //   await queryFulfilled;
        // },

        //#region dispatch
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(setTableAreas(data.results ?? [])); // store in slice
          } catch (err) {
            console.error('Failed to fetch table areas:', err);
          }
        },
        //#endregion dispatch

        transformResponse: (response: IApiResponse<IAreaGroupInterface>) =>
          response?.data,
        forceRefetch: () => true,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      getAllAreaTableList: builder.query({
        query: (params?: string | null) => ({
          url: 'tables',
          headers: {
            tableAreaID: params,
          },
        }),
        providesTags: ['TableAreaList'],
        // async onQueryStarted(_arg, { queryFulfilled }) {
        //   await queryFulfilled;
        // },

        //#region dispatch
        async onQueryStarted(_, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(setTableList(data.results ?? [])); // store in slice
          } catch (err) {
            console.error('Failed to fetch table list:', err);
          }
        },
        //#endregion dispatch

        transformResponse: (response: IApiResponse<ITableAreaInterface>) =>
          response?.data,
        forceRefetch: () => true,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      getTableDetails: builder.query({
        query: (params?: string | null) => `tables/${params}/details`,
        providesTags: ['TableDetails'],
        async onQueryStarted(_, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;

            if (!data) return;

            dispatch(
              setTableDetails(
                data.results?.[data.results?.length - 1] ?? undefined,
              ),
            ); // store in slice
          } catch (err) {
            console.error('Failed to fetch table areas:', err);
          } finally {
            dispatch(stopIsLoadingTransferTable());
          }
        },
        transformResponse: (
          response: IApiResponse<IPOSTableDetailsInterface>,
        ) => response?.data,
        forceRefetch: () => true,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      getAllTableGroupListWithParams: builder.query({
        query: ({
          tableAreaID,
          params,
        }: {
          tableAreaID: string;
          params?: 'All' | 0 | 1;
        }) => ({
          url: `tables/groupings/${tableAreaID}?filter[type]=${params}`,
        }),
        providesTags: ['TableGroupDetails'],
        async onQueryStarted(_arg, { queryFulfilled }) {
          await queryFulfilled;
        },
        transformResponse: (response: IApiResponse<IAreaGroupInterface>) =>
          response?.data,
        forceRefetch: () => true,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      createTableGroup: builder.mutation({
        query: (payload: CreateUpdateTableAreaPayload) => ({
          url: 'tables/groupings',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(tableGroupAPI.util.invalidateTags(['TableGroupList']));
            }
          } catch (error) {}
        },
        transformResponse: (response: { data: string }) => response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      updateTableGroup: builder.mutation({
        query: ({
          refId,
          payload,
        }: {
          refId: string;
          payload: CreateUpdateTableAreaPayload;
        }) => ({
          url: `tables/groupings/${refId}`,
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(tableGroupAPI.util.invalidateTags(['TableGroupList']));
          }
        },
        transformResponse: (response: { data: string }) => response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      updateTableList: builder.mutation({
        query: ({ payload }: { payload: CreateUpdateTablesListPayload }) => ({
          url: `tables`,
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(tableGroupAPI.util.invalidateTags(['TableAreaList']));
          }
        },
        transformResponse: (response: { data: string }) => response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
    }),
  });
// #endregion API Endpoints

// #region Export Hooks
export const {
  useGetAllTableGroupListQuery,
  useGetAllAreaTableListQuery,
  useGetTableDetailsQuery,
  useLazyGetTableDetailsQuery,
  useGetAllTableGroupListWithParamsQuery,
  useCreateTableGroupMutation,
  useUpdateTableGroupMutation,
  useUpdateTableListMutation,
} = tableGroupAPI;
// #endregion Export Hooks
